import PropTypes from 'prop-types'
import { kea } from 'kea'
import Bugsnag from '~/scenes/bugsnag'
import { put, call } from 'redux-saga/effects'
import scrollToError from 'lib/utils/scroll-to-error'
import RawField from 'lib/kea/field'
import RawAdminField from 'lib/kea/admin-field'

const noopO = {}
const noopA = []
const noop = () => noopO
const noopArray = () => noopA

export default function createForm (options) {
  const noField = options.noField || false
  const connect = options.connect || undefined
  const key = options.key || undefined
  const path = options.path || undefined
  const propType = options.propTypes ? PropTypes.shape(options.propTypes) : PropTypes.object
  const defaults = options.defaults || {}
  const setInitialState = options.setInitialState || {}
  const validate = options.validate || noop
  const selectFieldsInput = options.selectFieldsInput || undefined
  const selectFields = options.selectFields || noopArray
  const validationInput = options.validationInput || null
  const storeChangesOnSave = typeof options.storeChangesOnSave !== 'undefined' ? options.storeChangesOnSave : true

  const submitWhenNothingChanged = options.submitWhenNothingChanged || false
  const dispatchSuccessAfterSubmit =
    typeof options.dispatchSuccessAfterSubmit !== 'undefined' ? options.dispatchSuccessAfterSubmit : true
  const manualValidation = options.manualValidation || noop
  const inContainer = options.inContainer || null // limit errors to e.g. #general-form

  const autoSave = options.autoSave || noop
  const submit = options.submit || noop
  const success = options.success || noop
  const failure = options.failure || noop

  const response = kea({
    connect,
    key,
    path,

    actions: () => ({
      enableIsAutoSaving: true,
      disableIsAutoSaving: true,
      autoSave: true,
      setInitialState: initialState => ({ initialState }),
      setAutoSavedState: autoSavedState => ({ autoSavedState }),
      setValue: (key, value) => ({ key, value }),
      setValues: values => ({ values }),
      clearChanges: true,

      startManualValidation: (field = 'validating') => ({ field }),
      endManualValidation: true,

      setErrors: errors => ({ errors }),
      mergeErrors: errors => ({ errors }),
      clearErrors: true,
      showErrors: true,
      showErrorsForField: field => ({ field }),
      clearErrorsForField: field => ({ field }),

      submit: true,
      cancelAndRedirect: redirectUrl => ({ redirectUrl }),
      submitSuccess: (response, values) => ({ response, values }),
      submitFailure: error => ({ error }),
      toggleRedirecting: status => ({ status })
    }),

    defaults: args => state => {
      const obj = typeof defaults === 'function' ? defaults(state) : defaults
      return {
        initialState: typeof obj === 'function' ? defaults(args) : obj
      }
    },

    reducers: ({ actions }) => ({
      initialState: [
        {},
        PropTypes.object,
        Object.assign(
          {
            [actions.setInitialState]: (_, payload) => payload.initialState
          },
          setInitialState
        )
      ],

      isAutoSaving: [
        false,
        PropTypes.bool,
        {
          [actions.enableIsAutoSaving]: () => true,
          [actions.disableIsAutoSaving]: () => false
        }
      ],

      autoSavedState: [
        {},
        PropTypes.object,
        Object.assign(
          {
            [actions.setInitialState]: (_, payload) => payload.initialState,
            [actions.setAutoSavedState]: (_, payload) => payload.autoSavedState
          },
          setInitialState
        )
      ],

      changes: [
        {},
        PropTypes.object,
        {
          [actions.setValue]: (state, payload) => {
            return Object.assign({}, state, { [payload.key]: payload.value })
          },
          [actions.setValues]: (state, payload) => {
            return Object.assign({}, state, payload.values)
          },
          [actions.submitSuccess]: () => ({}),
          [actions.clearChanges]: () => ({})
        }
      ],

      isRedirecting: [
        false,
        PropTypes.bool,
        {
          [actions.toggleRedirecting]: (_, payload) => payload.status
        }
      ],

      isCanceling: [
        false,
        PropTypes.bool,
        {
          [actions.cancelAndRedirect]: () => true
        }
      ],

      isSubmitting: [
        false,
        PropTypes.bool,
        {
          [actions.submit]: () => true,
          [actions.submitSuccess]: () => false,
          [actions.submitFailure]: () => false
        }
      ],

      isValidating: [
        null,
        PropTypes.string,
        {
          [actions.startManualValidation]: (_, payload) => payload.field,
          [actions.endManualValidation]: () => null
        }
      ],

      manualErrors: [
        {},
        PropTypes.object,
        {
          [actions.setErrors]: (_, payload) => payload.errors,
          [actions.clearErrors]: () => ({}),
          [actions.mergeErrors]: (state, payload) => ({ ...state, ...payload.errors }),
          [actions.setValue]: (state, payload) => {
            if (Object.keys(state).length > 0) {
              const { [payload.key]: discard, ...rest } = state
              return rest
            } else {
              return state
            }
          },
          [actions.submitSuccess]: () => ({}),
          [actions.clearChanges]: () => ({})
        }
      ],

      showErrors: [
        false,
        PropTypes.bool,
        {
          [actions.submit]: () => true,
          [actions.submitSuccess]: () => false,
          [actions.clearChanges]: () => false,
          [actions.showErrors]: () => true
        }
      ],

      showErrorsForFields: [
        [],
        PropTypes.array,
        {
          [actions.showErrorsForField]: (state, payload) =>
            state.filter(f => f !== payload.field).concat([payload.field]),
          [actions.clearErrorsForField]: (state, payload) => state.filter(f => f !== payload.field),
          [actions.submit]: () => [],
          [actions.submitSuccess]: () => [],
          [actions.clearChanges]: () => [],
          [actions.showErrors]: () => []
        }
      ]
    }),

    selectors: ({ selectors }) => ({
      values: [
        () => [selectors.initialState, selectors.changes],
        (initialState, changes) => Object.assign({}, initialState, changes),
        propType
      ],

      hasChanges: [
        () => [selectors.initialState, selectors.changes],
        (initialState, changes) => {
          return Object.keys(changes).filter(k => changes[k] !== initialState[k]).length > 0
        },
        PropTypes.bool
      ],

      fields: [
        selectFieldsInput ? () => selectFieldsInput(selectors) : () => [selectors.values],
        selectFields,
        PropTypes.array
      ],

      validationErrors: [
        () => (validationInput ? validationInput(selectors) : [selectors.values]),
        validate,
        PropTypes.object
      ],

      hasErrors: [
        () => [selectors.validationErrors, selectors.manualErrors],
        (validationErrors, manualErrors) =>
          Object.values(validationErrors).filter(k => k).length > 0 ||
          Object.values(manualErrors).filter(k => k).length > 0,
        PropTypes.bool
      ],

      canSubmit: [
        () => [selectors.hasErrors, selectors.hasChanges, selectors.isValidating],
        (hasErrors, hasChanges, isValidating) =>
          !isValidating && !hasErrors && (submitWhenNothingChanged || hasChanges),
        PropTypes.bool
      ],

      hasAfterAutoSaveChanges: [
        () => [selectors.autoSavedState, selectors.changes],
        (autoSavedState, changes) => {
          return Object.keys(changes).filter(k => changes[k] !== autoSavedState[k]).length > 0
        },
        PropTypes.bool
      ],

      canAutoSave: [
        () => [
          selectors.hasChanges,
          selectors.isValidating,
          selectors.hasAfterAutoSaveChanges,
          selectors.isAutoSaving,
          selectors.isSubmitting
        ],
        (hasChanges, isValidating, hasAfterAutoSaveChanges, isAutoSaving, isSubmitting) =>
          !isValidating && hasChanges && hasAfterAutoSaveChanges && !isAutoSaving && !isSubmitting,
        PropTypes.bool
      ],

      currentErrors: [
        () => [selectors.validationErrors, selectors.manualErrors],
        (validationErrors, manualErrors) => ({ ...validationErrors, ...manualErrors }),
        PropTypes.object
      ],

      errors: [
        () => [selectors.validationErrors, selectors.manualErrors, selectors.showErrors, selectors.showErrorsForFields],
        (validationErrors, manualErrors, showErrors, showErrorsForFields) => {
          if (showErrors || showErrorsForFields.length > 0) {
            const errors = { ...validationErrors, ...manualErrors }

            if (showErrors) {
              return errors
            } else {
              const fieldErrors = {}
              for (const field of showErrorsForFields) {
                fieldErrors[field] = errors[field]
              }
              return fieldErrors
            }
          }
          return {}
        },
        PropTypes.object
      ]
    }),

    takeLatest: ({ actions, workers }) =>
      Object.assign(
        {
          [actions.autoSave]: function * (action) {
            try {
              const { setAutoSavedState, enableIsAutoSaving, disableIsAutoSaving } = this.actions

              const canAutoSave = yield this.get('canAutoSave')
              const currentErrors = yield this.get('currentErrors')
              const values = yield this.get('values')
              const initialState = yield this.get('initialState')

              if (canAutoSave) {
                yield put(enableIsAutoSaving())
                const cleanValues = {}

                Object.keys(values).forEach(k => {
                  cleanValues[k] = currentErrors[k] ? initialState[k] : values[k]
                })
                const response = yield call(autoSave.bind(this), values)
                if (response.success) {
                  yield put(setAutoSavedState(values))
                }
              }
              yield put(disableIsAutoSaving())
            } catch (error) {
              Bugsnag.notify(error)
            }
          },
          [actions.submit]: function * () {
            const { submitSuccess, submitFailure, setInitialState } = this.actions

            const canSubmit = yield this.get('canSubmit')

            if (!canSubmit) {
              yield put(submitFailure())

              if (inContainer) {
                const container = typeof inContainer === 'function' ? inContainer(this) : inContainer
                scrollToError(container)
              }

              return
            }
            try {
              const values = yield this.get('values')
              const response = yield call(submit.bind(this))
              if (dispatchSuccessAfterSubmit) {
                if (storeChangesOnSave) {
                  yield put(setInitialState(values))
                }
                yield put(submitSuccess(response, values))
                yield call(success.bind(this), response, values)
              }
            } catch (error) {
              yield put(submitFailure(error))
              yield call(failure.bind(this), error)
            }
          },
          [actions.cancelAndRedirect]: function * (action) {
            try {
              const redirectUrl = action.payload.redirectUrl
              window.location.href = redirectUrl
              window.setTimeout(() => {
                window.location.href = redirectUrl
              }, 15000)
            } catch (error) {
              yield call(failure.bind(this), error)
            }
          }
        },
        manualValidation
          ? {
              [actions.setValue]: function * (action) {
                const { startManualValidation, endManualValidation } = this.actions
                const { key, value } = action.payload

                yield put(startManualValidation(key))
                yield call(manualValidation.bind(this), key, value)
                yield put(endManualValidation())
              }
            }
          : {}
      )
  })

  if (!noField) {
    response.getField = () => response(RawField)
    response.getAdminField = () => response(RawAdminField)
  }

  return response
}
